/**
 * Crippleloader Component
 * A ripple loading animation using Framer Motion
 * Based on https://examples.motion.dev/react/loading-ripple
 */
'use client';
import { motion } from 'framer-motion';
import type { CSSProperties } from 'react';
import { ColorType, getColorValue } from '@/theme/colors';
import { dataEl, type DataElScope } from '@/utils/dataEl';
export interface CrippleloaderProps {
    dataElScope: DataElScope;
    width?: number; // Max width in pixels
    color?: ColorType;
    pace?: number; // Animation speed multiplier (default: 0.6, higher = faster)
    className?: string;
    style?: CSSProperties;
}
export function Crippleloader({ dataElScope, width = 40, color = ColorType.Accent, pace = 1.1, className, style, }: CrippleloaderProps) {
    const containerStyle: CSSProperties = {
        position: 'relative',
        width: width,
        height: width,
        aspectRatio: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...style,
    };
    // Base duration adjusted by pace (lower pace = slower animation)
    const baseDuration = 2 / pace;
    const delayBetweenRipples = 0.5 / pace;
    return (
    <div data-el={dataEl(dataElScope, 'Root')} className={className} style={containerStyle}>
      {/* Ripple 1 */}
      <motion.span style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${getColorValue(color, 1)}`,
        }} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: [0, 1, 0] }} transition={{
            duration: baseDuration,
            repeat: Infinity,
            ease: 'easeOut',
        }}/>

      {/* Ripple 2 */}
      <motion.span style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${getColorValue(color, 1)}`,
        }} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: [0, 1, 0] }} transition={{
            duration: baseDuration,
            repeat: Infinity,
            ease: 'easeOut',
            delay: delayBetweenRipples,
        }}/>

      {/* Ripple 3 */}
      <motion.span style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${getColorValue(color, 1)}`,
        }} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: [0, 1, 0] }} transition={{
            duration: baseDuration,
            repeat: Infinity,
            ease: 'easeOut',
            delay: delayBetweenRipples * 2,
        }}/>

      {/* Ripple 4 */}
      <motion.span style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${getColorValue(color, 1)}`,
        }} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: [0, 1, 0] }} transition={{
            duration: baseDuration,
            repeat: Infinity,
            delay: delayBetweenRipples * 3,
        }}/>
    </div>
    );
}
